import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Compass, Save } from 'lucide-react';
import { useMomentSearch } from '@/api/hooks';
import { errorMessage } from '@/api/client';
import type { FacetName, MomentSearchParams } from '@/api/types';
import { FACET_NAMES } from '@/api/types';
import { PageHeading } from '@/app/PageHeading';
import { Alert } from '@/components/arc/alert/alert';
import { Button } from '@/components/arc/button/button';
import { ChipGroup } from '@/components/arc/chip-group/chip-group';
import { EmptyState } from '@/components/arc/empty-state/empty-state';
import { Pagination } from '@/components/arc/pagination/pagination';
import { SearchField } from '@/components/arc/search-field/search-field';
import { Skeleton } from '@/components/arc/skeleton/skeleton';
import { Switch } from '@/components/arc/switch/switch';
import { DEFAULT_SIMILARITY, SimilarityControl } from '@/components/rules/SimilarityControl';
import { useDebounce } from '@/lib/hooks';
import { fmtNumber } from '@/lib/format';
import { rulesFromExplore } from '@/lib/rulesFromExplore';
import { useHref } from '@/module/basePath';
import { ExploreHitCard } from './ExploreHitCard';
import styles from './ExplorePage.module.css';

const LIMIT = 48;
const MAX_FACET_OPTIONS = 20;

type Facets = Record<FacetName, string[]>;
const emptyFacets = (): Facets => ({
  topics: [],
  brands: [],
  setting: [],
  sentiment: [],
  iab: [],
  risk: [],
  channel: [],
});
const FACET_LABEL: Record<FacetName, string> = {
  topics: 'Topics',
  brands: 'Brands',
  setting: 'Setting',
  sentiment: 'Sentiment',
  iab: 'IAB categories',
  risk: 'Brand safety risk',
  channel: 'Channel',
};

export function ExplorePage() {
  const nav = useNavigate();
  const href = useHref();
  const [q, setQ] = useState('');
  const dq = useDebounce(q.trim(), 400);
  const [semantic, setSemantic] = useState(false);
  const [threshold, setThreshold] = useState(DEFAULT_SIMILARITY);
  const dth = useDebounce(threshold, 300);
  const [facets, setFacets] = useState<Facets>(emptyFacets);
  const [page, setPage] = useState(1);

  const useSemantic = semantic && dq.length > 0;
  const params = useMemo<MomentSearchParams>(
    () => ({
      q: dq || undefined,
      semantic: useSemantic,
      threshold: useSemantic ? dth : undefined,
      limit: LIMIT,
      offset: (page - 1) * LIMIT,
      ...facets,
    }),
    [dq, useSemantic, dth, page, facets],
  );
  const res = useMomentSearch(params);

  const changeQuery = (v: string) => {
    setQ(v);
    setPage(1);
  };
  const changeSemantic = (v: boolean) => {
    setSemantic(v);
    setPage(1);
  };
  const changeFacet = (f: FacetName, v: string[]) => {
    setFacets((p) => ({ ...p, [f]: v }));
    setPage(1);
  };
  const clearFacets = () => {
    setFacets(emptyFacets());
    setPage(1);
  };

  const activeCount = Object.values(facets).reduce((a, b) => a + b.length, 0);
  const total = res.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / LIMIT));
  const hits = res.data?.hits ?? [];

  const saveAsSegment = () => {
    const rules = rulesFromExplore({ q: dq, semantic: useSemantic, threshold, facets });
    nav(href('/segments/new'), {
      state: { prefill: { name: dq ? `Explore: ${dq}` : 'Explore segment', rules, granularity: 'moment' } },
    });
  };

  const facetSections = FACET_NAMES.map((f) => {
    const dist = res.data?.facets?.[f] ?? {};
    const entries = Object.entries(dist)
      .sort((a, b) => b[1] - a[1])
      .slice(0, MAX_FACET_OPTIONS);
    const selectedMissing = facets[f].filter((v) => !(v in dist)).map((v) => [v, 0] as [string, number]);
    const options = [...selectedMissing, ...entries].map(([v, n]) => ({
      value: v,
      label: n ? `${v} (${n})` : v,
    }));
    if (options.length === 0) return null;
    return (
      <div key={f} className={styles.facet}>
        <h3 className={styles.facetTitle}>{FACET_LABEL[f]}</h3>
        <ChipGroup
          label={FACET_LABEL[f]}
          multiple
          maxVisible={8}
          options={options}
          value={facets[f]}
          onValueChange={(v) => changeFacet(f, v)}
        />
      </div>
    );
  });
  const hasFacets = facetSections.some(Boolean);

  return (
    <main className="page">
      <PageHeading
        title="Explore"
        description="Search moments across every analyzed video, narrow them by facets, then save the filter as a segment."
        actions={
          <Button type="button" onClick={saveAsSegment} disabled={!dq && activeCount === 0}>
            <Save size={16} strokeWidth={1.75} aria-hidden="true" />
            Save as segment
          </Button>
        }
        below={
          <div className={styles.toolbar}>
            <div className={styles.search}>
              <SearchField
                label="Search moments"
                value={q}
                onValueChange={changeQuery}
                placeholder={
                  semantic
                    ? 'Describe the moment, e.g. cozy family breakfast at home'
                    : 'Search transcripts, topics, brands'
                }
              />
            </div>
            <Switch label="Semantic search" checked={semantic} onCheckedChange={changeSemantic} />
            {semantic ? (
              <div className={styles.threshold}>
                <SimilarityControl
                  label="Minimum similarity"
                  value={threshold}
                  onValueChange={setThreshold}
                  onValueCommit={() => setPage(1)}
                />
              </div>
            ) : null}
            <p className={`${styles.count} text-secondary`} aria-live="polite">
              {res.data ? `${fmtNumber(total)} ${total === 1 ? 'moment' : 'moments'}` : ''}
              {res.data && res.isFetching ? ', updating' : ''}
            </p>
          </div>
        }
      />

      <div className={styles.layout}>
        <aside className={styles.aside} aria-label="Filters">
          <div className={styles.asideHead}>
            <h2 className={styles.asideTitle}>Filters</h2>
            {activeCount > 0 ? (
              <Button type="button" variant="ghost" size="sm" onClick={clearFacets}>
                Clear {activeCount} {activeCount === 1 ? 'filter' : 'filters'}
              </Button>
            ) : null}
          </div>
          {res.isLoading ? (
            <Skeleton label="Loading filters" lines={4} />
          ) : hasFacets ? (
            facetSections
          ) : (
            <p className="text-muted">Facets appear once there are moments to filter.</p>
          )}
        </aside>

        <section className={styles.results} aria-label="Results" aria-busy={res.isLoading || undefined}>
          {res.isError ? (
            <div className="stack-4">
              <Alert tone="danger" title="We could not load moments">
                {errorMessage(res.error)}
              </Alert>
              <div>
                <Button type="button" variant="secondary" onClick={() => void res.refetch()}>
                  Try again
                </Button>
              </div>
            </div>
          ) : res.isLoading ? (
            <div className={styles.grid}>
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} label="Loading moment" lines={4} />
              ))}
            </div>
          ) : hits.length === 0 ? (
            <EmptyState
              icon={<Compass size={24} strokeWidth={1.5} aria-hidden="true" />}
              title="No moments match"
              description={
                semantic
                  ? 'Try a lower similarity threshold, fewer filters, or a different description.'
                  : 'Try fewer filters, a broader query, or turn on semantic search.'
              }
              action={
                activeCount > 0 ? (
                  <Button type="button" variant="secondary" onClick={clearFacets}>
                    Clear filters
                  </Button>
                ) : !semantic && dq ? (
                  <Button type="button" variant="secondary" onClick={() => changeSemantic(true)}>
                    Turn on semantic search
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="stack-6">
              <div className={styles.grid}>
                {hits.map((h) => (
                  <ExploreHitCard
                    key={h.id}
                    hit={h}
                    href={href(`/videos/${h.video_id}?t=${Math.floor(h.start_sec)}`)}
                  />
                ))}
              </div>
              {pageCount > 1 ? (
                <div className={styles.pagination}>
                  <Pagination page={page} pageCount={pageCount} onPageChange={setPage} label="Result pages" />
                </div>
              ) : null}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
